import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import Layout from "@/components/Layout";
import { OnboardingTour, shouldShowTour } from "@/components/OnboardingTour";
import Landing from "@/pages/landing";
import Login from "@/pages/login";
import Onboarding from "@/pages/onboarding";
import Dashboard from "@/pages/dashboard";
import Templates from "@/pages/templates";
import Speakers from "@/pages/speakers";
import CalendarPage from "@/pages/calendar";
import PlatformsPage from "@/pages/platforms";
import AnalyticsPage from "@/pages/analytics";
import Settings from "@/pages/settings";
import NotFound from "@/pages/not-found";
import type { Organization, OrganizationRole } from "@shared/schema";

function Router() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Fetch user's organizations if authenticated
  const { data: organizations, isLoading: orgsLoading } = useQuery<Array<Organization & { role: OrganizationRole }>>({
    queryKey: ["/api/organizations"],
    enabled: isAuthenticated,
  });

  const isLoading = authLoading || (isAuthenticated && orgsLoading);
  const hasOrganization = organizations && organizations.length > 0;

  // Show loading or handle unauthenticated users
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/" component={Landing} />
        <Route component={() => <Landing />} />
      </Switch>
    );
  }

  // User is authenticated but has no organization - always show onboarding
  if (!hasOrganization) {
    return <Onboarding />;
  }

  // User is authenticated and has organization - show full app
  return (
    <Switch>
      <Route path="/" component={Dashboard} />
      <Route path="/dashboard" component={Dashboard} />
      <Route path="/templates" component={Templates} />
      <Route path="/speakers" component={Speakers} />
      <Route path="/calendar" component={CalendarPage} />
      <Route path="/platforms" component={PlatformsPage} />
      <Route path="/analytics" component={AnalyticsPage} />
      <Route path="/settings" component={Settings} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Fetch user's organizations if authenticated
  const { data: organizations, isLoading: orgsLoading } = useQuery<Array<Organization & { role: OrganizationRole }>>({
    queryKey: ["/api/organizations"],
    enabled: isAuthenticated,
  });

  const isLoading = authLoading || (isAuthenticated && orgsLoading);
  const hasOrganization = organizations && organizations.length > 0;

  // Show landing or onboarding page without layout for logged-out users or users without organizations
  if (isLoading || !isAuthenticated || !hasOrganization) {
    return <Router />;
  }

  // Show app with layout for authenticated users with organizations
  return (
    <Layout>
      <Router />
      {shouldShowTour() && <OnboardingTour autoStart={true} />}
    </Layout>
  );
}

export default App;
